import React, { useState, useRef, RefObject, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import useOnClickOutside from "src/app/methods/useOnClickOutside";
import { RootState } from "src/redux/reducers";
import { navigateToCreateNewProject } from "src/app/SettingsModal/methods";
import { setCreatorSearchLockedModalVisibility } from "src/redux";
import { ReactComponent as CompassIcon } from "src/images/compass.svg";
import { ReactComponent as ThunderIcon } from "src/images/thunder-move.svg";

const OFFSET_VALUE = 54;
const INITIAL_WIDTH = 185;

interface QuickActionsMenuProps {
  sidebarRef: RefObject<HTMLDivElement>;
  topPosition: number;
  onClose: () => void;
}

export default function QuickActionsMenu({
  sidebarRef,
  topPosition,
  onClose,
}: QuickActionsMenuProps) {
  const [width, setWidth] = useState(INITIAL_WIDTH);
  const menuRef = useRef<HTMLDivElement>(null);
  const history = useHistory();

  const dispatch = useDispatch();

  const { activeWorkspaceUuid, identity } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  useOnClickOutside(menuRef, onClose);

  useEffect(() => {
    const element = sidebarRef.current;
    if (!element) return;

    setWidth(element.getBoundingClientRect().width);

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [sidebarRef]);

  const handleCreateProject = () => {
    onClose();
    navigateToCreateNewProject(history);
  };

  const handleCreatorSearchClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    onClose();
    if (identity?.isDemoWorkspace) {
      event.preventDefault();
      dispatch(setCreatorSearchLockedModalVisibility(true));
    }
  };

  return createPortal(
    <div
      ref={menuRef}
      className="quick-actions__menu"
      data-qa="quick-actions-sidebar-menu"
      onClick={(e) => e.stopPropagation()}
      style={{
        top: topPosition,
        left: width + OFFSET_VALUE,
      }}
    >
      <div className="quick-actions__header">
        <h2 className="text-h7-semibold">
          <IDHFormattedMessage
            id="ws_what_do_you_want_to_do"
            defaultMessage="What do you want to do?"
          />
        </h2>
      </div>
      <ul className="quick-actions__list">
        <Link
          onClick={handleCreatorSearchClick}
          to={`/workspace/${activeWorkspaceUuid}/campaign-creator-search`}
        >
          <li className="quick-actions__content">
            <span className="quick-actions__icon">
              <CompassIcon />
            </span>
            <div className="quick-actions__text">
              <h3 className="text-h7-regular">
                <IDHFormattedMessage
                  id="ws_explore_creators_quick_start_title"
                  defaultMessage="Search creators"
                />
              </h3>
              <p className="quick-actions__description">
                <IDHFormattedMessage
                  id="ws_explore_creators_quick_start_description"
                  defaultMessage="Discover creators using smart, AI-driven search."
                />
              </p>
            </div>
          </li>
        </Link>

        <li onClick={handleCreateProject} className="quick-actions__content">
          <span className="quick-actions__icon">
            <ThunderIcon className="icon-dark" />
          </span>
          <div className="quick-actions__text">
            <h3 className="text-h7-regular">
              <IDHFormattedMessage
                id="ws_create_campaign_quick_start_title"
                defaultMessage="Create campaign"
              />
            </h3>
            <p className="quick-actions__description">
              <IDHFormattedMessage
                id="ws_create_campaign_quick_start_description"
                defaultMessage="Enter campaign details to generate a tailored template for platforms and deliverables."
              />
            </p>
          </div>
        </li>
      </ul>
    </div>,
    document.querySelector(".Overlays")!,
  );
}
